import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { getTaux } from '../../data/tarifsData';
import './Digitalisation.css';

const DEVISES = [
  { key: 'fcfa', label: 'FCFA', symbol: 'FCFA' },
  { key: 'usd',  label: 'USD',  symbol: '$' },
  { key: 'eur',  label: 'EUR',  symbol: '€' },
];

// ── Conversion LIVE à partir du FCFA (source unique de vérité) ──
// On ignore volontairement pack.prixUsd / pack.prixEur : ce sont des
// valeurs figées au moment de la rédaction des données, elles ne
// suivent jamais le taux BCE réel et finissent par diverger du
// taux affiché ailleurs sur le site (Tarifs, bandeau, etc.).
function fmt(fcfa, devise, TAUX) {
  if (!fcfa) return 'Sur devis';
  if (devise === 'usd') return Math.round(fcfa / TAUX.USD).toLocaleString('fr-FR') + ' $';
  if (devise === 'eur') return Math.round(fcfa / TAUX.EUR).toLocaleString('fr-FR') + ' €';
  return Math.round(fcfa).toLocaleString('fr-FR') + ' FCFA';
}

export default function Digitalisation({ onDevis }) {
  const { data } = useApp();
  const dig = data.digitalisation;
  const [secteurActif, setSecteurActif] = useState(0);
  const [devise, setDevise] = useState('fcfa');
  const [packOuvert, setPackOuvert] = useState(null);

  if (!dig) return null;
  const secteur = dig.secteurs[secteurActif];

  // Taux live — recalculé à chaque rendu depuis data.tauxChange,
  // exactement comme dans Tarifs.jsx. Garantit que le montant affiché
  // ici correspond TOUJOURS au taux annoncé partout ailleurs.
  const TAUX = getTaux(data.tauxChange || {});

  function prix(pack)      { return fmt(pack.prixFcfa,      devise, TAUX); }
  function entretien(pack) { return fmt(pack.entretienFcfa, devise, TAUX); }

  return (
    <section id="digitalisation" className="dig-section">
      {/* En-tête */}
      <div className="dig-header">
        <div className="dig-badge">🚀 TRANSFORMATION NUMÉRIQUE</div>
        <h2 className="dig-title">{dig.titre}</h2>
        <p className="dig-subtitle">{dig.sousTitre}</p>
        <p className="dig-desc">{dig.description}</p>
      </div>

      {/* Sélecteur devise */}
      <div className="dig-controls">
        <div className="dig-devise-btns">
          {DEVISES.map(d => (
            <button
              key={d.key}
              className={`dig-devise-btn ${devise === d.key ? 'active' : ''}`}
              onClick={() => setDevise(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>
        {/* Bandeau taux live — même info que sur Tarifs, pour cohérence visuelle */}
        <div style={{
          fontSize: '11px', color: TAUX.auto ? '#00CC88' : 'var(--muted)',
          marginTop: '8px', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '6px'
        }}>
          {TAUX.auto ? '🟢' : '⚙️'}
          {TAUX.auto
            ? `Taux BCE automatiques · 1€ = ${TAUX.EUR} FCFA · 1$ = ${TAUX.USD} FCFA`
            : `Taux indicatifs · 1€ ≈ ${TAUX.EUR} FCFA · 1$ ≈ ${TAUX.USD} FCFA`
          }
        </div>
      </div>

      {/* Onglets secteurs */}
      <div className="dig-secteurs">
        {dig.secteurs.map((s, i) => (
          <button
            key={s.id}
            className={`dig-secteur-btn ${secteurActif === i ? 'active' : ''}`}
            onClick={() => { setSecteurActif(i); setPackOuvert(null); }}
          >
            <span className="dig-s-icon">{s.icon}</span>
            <span className="dig-s-name">{s.name}</span>
          </button>
        ))}
      </div>

      {/* Contenu secteur */}
      <div className="dig-content">
        {/* Description secteur */}
        <div className="dig-secteur-desc">
          <h3>{secteur.icon} {secteur.name}</h3>
          <p>{secteur.desc}</p>
          <div className="dig-outils">
            {secteur.outils.map((o, i) => (
              <span key={i} className="dig-outil">✓ {o}</span>
            ))}
          </div>
        </div>

        {/* Packs */}
        <div className="dig-packs">
          {secteur.packs.map((pack, i) => (
            <div
              key={pack.id}
              className={`dig-pack ${packOuvert === i ? 'ouvert' : ''}`}
              style={{ '--pack-color': pack.color }}
            >
              {/* Badge */}
              {pack.badge && (
                <div className="dig-pack-badge" style={{ background: pack.color }}>
                  {pack.badge}
                </div>
              )}

              {/* En-tête pack */}
              <div className="dig-pack-head">
                <div className="dig-pack-name" style={{ color: pack.color }}>
                  {pack.name}
                </div>
                <div className="dig-pack-prix">
                  <span className="dig-prix-main">{prix(pack)}</span>
                  <span className="dig-prix-label">installation</span>
                </div>
                {pack.entretienActif && (
                  <div className="dig-pack-entretien">
                    <span className="dig-entretien-icon">🔧</span>
                    <span className="dig-entretien-txt">
                      Entretien : <strong style={{ color: pack.color }}>
                        {entretien(pack)}/mois
                      </strong>
                    </span>
                  </div>
                )}
                <p className="dig-pack-desc">{pack.desc}</p>
              </div>

              {/* Inclus */}
              <div className="dig-pack-inclus">
                <div
                  className="dig-inclus-toggle"
                  onClick={() => setPackOuvert(packOuvert === i ? null : i)}
                >
                  {packOuvert === i ? '▲ Masquer le détail' : '▼ Voir ce qui est inclus'}
                </div>
                {packOuvert === i && (
                  <ul className="dig-inclus-list">
                    {pack.inclus.map((item, j) => (
                      <li key={j}>
                        <span style={{ color: pack.color }}>✓</span> {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Boutons */}
              <div className="dig-pack-btns">
                <button
                  className="dig-btn-devis"
                  style={{ borderColor: pack.color, color: pack.color }}
                  onClick={() => onDevis && onDevis({
                    service: `Digitalisation ${secteur.name} — Pack ${pack.name}`,
                    message: `Bonjour,\n\nJe souhaite obtenir un devis pour :\n\n🌐 Digitalisation ${secteur.name}\n📦 Pack ${pack.name}\n💰 Prix installation : ${prix(pack)}\n${pack.entretienActif ? `🔧 Entretien mensuel : ${entretien(pack)}/mois\n` : ''}\nMerci de me contacter pour plus d'informations.`,
                  })}
                >
                  📋 Demander un devis
                </button>
                <button
                  className="dig-btn-contact"
                  style={{ background: pack.color }}
                  onClick={() => onDevis && onDevis({
                    service: `Digitalisation ${secteur.name} — Pack ${pack.name}`,
                    message: `Bonjour,\n\nJe souhaite être contacté concernant :\n\n🌐 Digitalisation ${secteur.name}\n📦 Pack ${pack.name}\n\nMerci.`,
                  })}
                >
                  💬 Nous contacter
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Note bas */}
      <div className="dig-note">
        <span>💡</span>
        <span>Tous les packs incluent une période de garantie et peuvent être personnalisés selon vos besoins spécifiques. L'entretien mensuel est optionnel et activable à tout moment après l'installation.</span>
      </div>
    </section>
  );
}